import { useState, useEffect, useContext, useRef } from 'react';
import clsx from 'clsx';
import StreamContext from 'utils/StreamContext';
import useUserIsFollowedByMe from 'sdk/hooks/follow/useUserIsFollowedByMe';
import useProfileById from 'sdk/hooks/user/useProfileById';
import useFollowUser from 'sdk/hooks/follow/useFollowUser';
import useUser from 'sdk/hooks/user/useProfile';
import { Link, useNavigate } from 'react-router-dom';
import useUserActivities from 'sdk/hooks/user/useUserActivities';
import useActivitiesCount from 'sdk/hooks/useActivitiesCount';
import styles from './Post.module.scss';
import ActionButtons from './ActionButtons';
import { ButtonWithIcon } from 'components/shared/ButtonWithIcon';
import { useSpring } from '@react-spring/web';
import { useActions } from 'store/useActions';
import ShareModal from './ShareModal';
import { ButtonWithIconUrlPosts } from 'components/shared/ButtonWithIconUrlPosts';
import useIsAuth from 'sdk/hooks/useIsAuth';
import { useTranslation } from 'react-i18next';
import useCommentsCount from 'sdk/hooks/useCommentsCount';
import { DEFAULT_AVATAR } from 'utils/Constants';

function BottomContent(props: any) {
  const { setSignUpVisible, videoProgressHovered } = props;
  const { client } = useContext(StreamContext);
  const data = props?.data;
  const post = data?.data;
  const profileData = useProfileById(post?.profile?.id);
  const image = profileData?.data?.avatar;
  const { data: isAuthData } = useIsAuth();
  const [hasOwnLike, setOwnLike] = useState(false);
  const [ownLike, setOwnLikeID] = useState('');
  const [isFollowedByMe, setIsFollowedByMe] = useState(false);
  const { setIsShareOpened, setIsSlideChange, setIsButtonMenuVisible } = useActions();
  const { t } = useTranslation() as any;
  const { data: currentUser } = useUser();

  const [{ y }, api] = useSpring(() => ({
    y: window.innerHeight,
    config: { tension: 300, friction: 30 },
  }));

  const { data: isFollowedByMeData, refetch: refetchIsFollowedByMe } = useUserIsFollowedByMe(
    post?.profile?.id,
  );
  const followToUser = useFollowUser(post?.profile?.id);

  useEffect(() => {
    setIsFollowedByMe(isFollowedByMeData ?? false);
  }, [isFollowedByMeData]);

  const forreignId = data?.foreign_id || '';

  const [likeCounts, setLikeCounts] = useState<number>(0);

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);

  const navigate = useNavigate();

  const { data: likeCountData, isFetched: likeCountFetched } = useActivitiesCount(
    forreignId,
    'like',
  );
  const { data: agreeCountData, isFetched: agreeCountFetched } = useActivitiesCount(
    forreignId,
    'agree',
  );
  const { data: disagreeCountData, isFetched: disagreeCountFetched } = useActivitiesCount(
    forreignId,
    'disagree',
  );
  const { data: commentCount } = useCommentsCount(post?.id);

  useEffect(() => {
    if (likeCountFetched && agreeCountFetched && disagreeCountFetched) {
      setLikeCounts(likeCountData ?? 0);
    }
  }, [likeCountData, agreeCountData, disagreeCountData]);

  const {
    data: reactions,
    refetch: refetchReactions,
    isFetching: reactionsIsLoading,
    isSuccess: reactionsIsSuccess,
  } = useUserActivities(currentUser?.id, forreignId);

  const isOwnLikeData = reactions?.filter(r => r.type === 'like');

  const openDrawer = () => {
    api.start({ y: 0 });
    setIsShareOpened(true);
    setIsSlideChange(false);
  };

  useEffect(() => {
    if (reactionsIsSuccess) {
      if (isOwnLikeData?.length) {
        setOwnLike(true);
        setOwnLikeID(isOwnLikeData?.[0]?.id);
      } else {
        setOwnLike(false);
        setOwnLikeID('');
      }
      if (!currentUser) {
        setOwnLike(false);
      }
    }
  }, [reactions, reactionsIsSuccess]);

  const [isCommentsVisible, setCommentsVisible] = useState(false);
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const descriptionRef = useRef<HTMLParagraphElement | null>(null);

  useEffect(() => {
    if (descriptionRef.current && post?.description) {
      const element = descriptionRef.current as HTMLParagraphElement;

      const tempElement = document.createElement('div');
      tempElement.style.visibility = 'hidden';
      tempElement.style.position = 'absolute';
      tempElement.style.font = window.getComputedStyle(element).font;
      tempElement.textContent = 'A';
      document.body.appendChild(tempElement);

      const lineHeight = tempElement.offsetHeight;
      document.body.removeChild(tempElement);

      const twoLinesHeight = lineHeight * 2;
      setNeedsExpansion(element.scrollHeight > twoLinesHeight + 2);
    }
  }, [post?.description]);

  const postData = data?.data; 

  const goToShopOrItem = () => {
    if (isAuthData?.isAuth) {
      if (!!postData?.product_id) {
        navigate(`/product/${postData?.product_id}`);
      } else if (!!postData?.salon_profile) {
        navigate(`/salon/${postData?.salon_profile}`);
      } else if (postData?.store_owners_profile) {
        navigate(`/store-profile/${postData?.store_owners_profile?.id}`);
      }
    } else {
      navigate('/sign-up');
    }
  };
  return (
    <>
      <div className={styles.containerBottom}>
        <div
          className={clsx(
            styles.containerInfo,
            videoProgressHovered && styles.containerInfo_videoProgressHovered,
          )}
        >
          <div className={styles.row}>
            <ActionButtons
              styles={styles}
              currentUser={currentUser}
              post={post}
              setSignUpVisible={setSignUpVisible}
              props={props}
              commentCount={commentCount}
              id={forreignId}
              openDrawer={openDrawer}
              openComments={() => setCommentsVisible(true)}
              setIsShareModalOpen={setIsShareModalOpen}
            />
          </div>
          <div className={clsx(styles.postUnderContainer)}>
            <div className={styles.descriptionWrapper}>
              <div className={styles.postTitleContainer}>
                <div className={styles.userImage}>
                  {post?.salon_profile ? (
                    <Link to={`/salon/${post?.salon_profile}`} className={styles.avatarContainer}>
                      <img
                        src={post?.salons_profile?.avatar || image || DEFAULT_AVATAR}
                        alt={post?.salons_profile?.avatar}
                      />
                    </Link>
                  ) : post?.store_owners_profile?.id ? (
                    <Link to={`/store-profile/${post?.store_owners_profile?.id}`} className={styles.avatarContainer}>
                      <img
                        src={post?.store_owners_profile?.avatar || image || DEFAULT_AVATAR}
                        alt={post?.store_owners_profile?.avatar}
                      />
                    </Link>
                  ) : (
                    <img
                      src={post?.salons_profile?.avatar || image || DEFAULT_AVATAR}
                      alt={post?.salons_profile?.avatar}
                    />
                  )}
                </div>

                <div className={styles.description}>
                  {post?.salon_profile ? (
                    <Link to={`/salon/${post?.salon_profile}`} className={styles.linktext}>
                      <p className={styles.postTitle}>
                        {post?.salons_profile?.name || post?.store_owners_profile?.store_name || ''}
                      </p>
                    </Link>
                  ) : post?.store_owners_profile?.id ? (
                    <Link to={`/store-profile/${post?.store_owners_profile?.id}`} className={styles.linktext}>
                      <p className={styles.postTitle}>
                        {post?.store_owners_profile?.store_name || ''}
                      </p>
                    </Link>
                  ) : (
                    <p className={styles.postTitle}>
                      {post?.salons_profile?.name || post?.store_owners_profile?.store_name || ''}
                    </p>
                  )}

                  <p
                    ref={descriptionRef}
                    className={clsx(
                      styles.postDescription,
                      !isDescriptionExpanded && styles.postDescriptionCollapsed,
                    )}
                    onClick={() =>
                      needsExpansion && setIsDescriptionExpanded(!isDescriptionExpanded)
                    }
                    style={{
                      maxHeight: isDescriptionExpanded ? '220px' : '60px',
                      overflow: 'hidden',
                    }}
                  >
                    {post?.description || ''}
                  </p>
                </div>
              </div>
            </div>

            {/* CTA кнопки */}
            <div className={clsx(styles.postButton)}>
              {!!postData?.product_id ? (
                <ButtonWithIconUrlPosts
                  func={() => {
                    goToShopOrItem();
                  }}
                  btnCallToActionName={t('Buy')}
                  disabled={false}
                />
              ) : !!data?.data?.salons_profile ? (
                <ButtonWithIcon
                  openBooking={props.openBooking}
                  disabled={!data?.data?.salons_profile}
                />
              ) : !!postData?.store_id || !!postData?.salon_profile ? (
                <ButtonWithIconUrlPosts
                  func={() => {
                    goToShopOrItem();
                  }}
                  btnCallToActionName={
                    !!postData?.store_id ? t('Shop') : ''
                  }
                  disabled={false}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {/* <PostComments
        commentsVisible={isCommentsVisible}
        onClose={() => setCommentsVisible(false)}
        postId={post.id}
        currentUser={currentUser}
      /> */}

      <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => {
            setIsShareModalOpen(false);
            setIsButtonMenuVisible(true);
          }}
          setIsShareModalOpen={setIsShareModalOpen}
          post={post}
      />
    </>
  );
}

export default BottomContent;
